'use client';

import React, { type ReactNode } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useTranslations } from 'next-intl';

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack ?? undefined },
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback message={this.state.error?.message ?? null} />;
    }

    return this.props.children;
  }
}

function ErrorFallback({ message }: { message: string | null }) {
  const t = useTranslations('errors.boundary');

  return (
    <div className="flex min-h-screen items-center justify-center bg-base-200 p-4">
      <div className="w-full max-w-md rounded-2xl border border-error/20 bg-base-100 p-6 shadow-lg">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-error"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-bold text-error">{t('title')}</h1>
            <p className="text-sm text-[var(--color-fg-muted)]">{t('description')}</p>
          </div>
        </div>

        {message ? (
          <div className="mb-4 rounded-lg bg-base-200 p-3">
            <p className="text-xs font-mono text-[var(--color-fg-muted)]">{message}</p>
          </div>
        ) : null}

        <button type="button" className="btn btn-primary btn-block" onClick={() => window.location.reload()}>
          {t('refresh')}
        </button>
      </div>
    </div>
  );
}
